import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatTile, CollapsibleSection } from "./_shared";
import ExecutiveDashboard from "./ExecutiveDashboard";
import InventoryBreakdown from "./InventoryBreakdown";
import ProfitLiquidityIntel from "./ProfitLiquidityIntel";
import PricingClusters from "./PricingClusters";
import MakeModelFrequency from "./MakeModelFrequency";
import DataQuality from "./DataQuality";
import RegistrationPrefixAnalytics from "./RegistrationPrefixAnalytics";
import CustomerIntentAnalytics from "./CustomerIntentAnalytics";

function ProductionAuditPanel() {
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.get("/admin/production-audit");
      setAudit(data);
    } catch (e) { toast.error("Audit failed"); }
    finally { setRunning(false); }
  };

  const cleanup = async () => {
    if (!window.confirm("Delete all test-pattern vehicles, buyer requirements, offers, matches, and notifications? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/cleanup-test-data", { confirm: true, dry_run: false });
      toast.success(`Removed ${data.vehicles} vehicles, ${data.requirements} requirements, ${data.offers + data.matches + data.notifications} dependent rows`);
      run();
    } catch (e) { toast.error("Cleanup failed"); }
  };

  const demoteOldAdmin = async () => {
    try {
      const { data } = await api.post("/admin/demote-old-admin");
      toast.success(data.demoted ? "Old admin demoted" : "No legacy admin found");
      run();
    } catch (e) { toast.error("Demotion failed"); }
  };

  return (
    <div className="qd-card p-5 mb-6" data-testid="production-audit-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="qd-overline">Production Audit</div>
          <div className="text-xs text-white/55 mt-1">Verify deployment hygiene — env vars, test data, CORS, admin user.</div>
        </div>
        <button onClick={run} disabled={running} data-testid="audit-run-btn" className="btn-secondary text-[10px] py-2 px-3">{running ? "Running…" : "Run Audit"}</button>
      </div>

      {audit && (
        <>
          <div className={`text-sm font-bold mb-3 ${audit.overall_ok ? "text-green-500" : "text-[#FF8C42]"}`}>
            {audit.overall_ok ? "✅ PRODUCTION READY" : "⚠️ ACTION REQUIRED"}
          </div>
          <div className="space-y-1.5 font-mono-data text-xs">
            {audit.checks.map(c => (
              <div key={c.key} className="flex items-start justify-between gap-3 border-b border-[#1A1A1A] py-1.5">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <span className="shrink-0 mt-0.5">{c.ok ? "✅" : "⚠️"}</span>
                  <div className="min-w-0">
                    <div className="text-white/85">{c.label}: <span className="text-white">{String(c.value)}</span></div>
                    {c.remedy && <div className="text-[10px] text-white/40 mt-0.5">{c.remedy}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-[#1A1A1A]">
            <button data-testid="audit-cleanup-btn" onClick={cleanup} className="btn-secondary text-[10px] py-2 px-3">Cleanup Test Data</button>
            <button data-testid="audit-demote-btn" onClick={demoteOldAdmin} className="btn-secondary text-[10px] py-2 px-3">Demote Old Admin</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Analytics() {
  const [funnel, setFunnel] = useState(null);
  const [eng, setEng] = useState(null);
  const [ms, setMS] = useState(null);
  const [notifs, setNotifs] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics/funnel").then(r => setFunnel(r.data)),
      api.get("/admin/analytics/engagement").then(r => setEng(r.data)),
      api.get("/admin/analytics/matching-success").then(r => setMS(r.data)),
      api.get("/admin/notifications/stats").then(r => setNotifs(r.data)),
    ]);
  }, []);

  return (
    <div data-testid="admin-analytics">
      <PageHeader title="Activity Analytics" subtitle="Conversion & Drop-offs" />

      {/* Q Drives Analytics V3 — Phase 1: Executive Summary + Capital Deployment (always open — exec-first per mobile spec) */}
      <CollapsibleSection
        title="Executive Summary & Capital Deployment"
        subtitle="Pipeline KPIs + Buy Now / Avoid leaderboards"
        defaultOpen
        testid="phase1-section"
      >
        <ExecutiveDashboard />
      </CollapsibleSection>

      {/* Data Quality — sits right under Exec so any normalization issues are visible immediately */}
      <CollapsibleSection
        title="Data Quality"
        subtitle="Make/Model alias normalization · raw vs canonical lead-entry hygiene"
        defaultOpen={false}
        testid="dq-section"
      >
        <DataQuality />
      </CollapsibleSection>

      {/* Q Drives Analytics V3 — Phase 2: Inventory Breakdown */}
      <CollapsibleSection
        title="Inventory Breakdown"
        subtitle="Brand · Year · Fuel · Transmission · Powertrain"
        defaultOpen
        testid="phase2-section"
      >
        <InventoryBreakdown />
      </CollapsibleSection>

      {/* Make & Model Frequency — sourcing-funnel concentration by exact model */}
      <CollapsibleSection
        title="Make & Model Frequency"
        subtitle="Which exact (make + model) combinations are entering the funnel most"
        defaultOpen
        testid="mmf-section"
      >
        <MakeModelFrequency />
      </CollapsibleSection>

      {/* Registration Prefix — Hyderabad vs District lead qualification */}
      <CollapsibleSection
        title="Registration Prefix"
        subtitle="Hyderabad priority vs other Telangana districts · conversion rate · top RTOs"
        defaultOpen
        testid="rp-section"
      >
        <RegistrationPrefixAnalytics />
      </CollapsibleSection>

      {/* Customer Intent — seller's stated goal from the Your Goal step */}
      <CollapsibleSection
        title="Customer Intent"
        subtitle="What sellers say they're looking for · leads · purchases · conversion rate"
        defaultOpen
        testid="ci-section"
      >
        <CustomerIntentAnalytics />
      </CollapsibleSection>

      {/* Q Drives Analytics V3 — Phase 3: Profitability & Liquidity Intelligence */}
      <CollapsibleSection
        title="Profitability & Liquidity Intelligence"
        subtitle="Liquidity bands · Gap distribution · Top deals · Green Return"
        defaultOpen
        testid="phase3-section"
      >
        <ProfitLiquidityIntel />
      </CollapsibleSection>

      {/* Q Drives Analytics V3 — Phase 4: Pricing & KM Clusters */}
      <CollapsibleSection
        title="Pricing & KM Clusters"
        subtitle="Asking · Offer · Retail · KM distributions"
        defaultOpen
        testid="phase4-section"
      >
        <PricingClusters />
      </CollapsibleSection>

      <CollapsibleSection
        title="Production Audit"
        subtitle="Deployment hygiene checks"
        defaultOpen={false}
        testid="audit-section"
      >
        <ProductionAuditPanel />
      </CollapsibleSection>

      {eng && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] mb-6">
          <StatTile label="New users (7d)" value={`+${eng.new_users_7d}`} accent />
          <StatTile label="New vehicles (7d)" value={`+${eng.new_vehicles_7d}`} />
          <StatTile label="New requirements (7d)" value={`+${eng.new_requirements_7d}`} />
          {notifs && <StatTile label="Notif open rate" value={`${notifs.open_rate}%`} accent />}
        </div>
      )}

      {/* Funnel chart */}
      {funnel && (
        <div className="qd-card p-6 mb-6">
          <div className="qd-overline mb-4">Sourcing Funnel · Cumulative</div>
          <div className="space-y-2">
            {Object.entries(funnel.reached).map(([k, v], i, arr) => {
              const max = Math.max(...arr.map(a => a[1]), 1);
              const prev = i > 0 ? arr[i-1][1] : v;
              const dropoff = prev > 0 ? Math.round(((prev - v) / prev) * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-3 text-xs font-mono-data">
                  <div className="w-44 text-white/80 uppercase tracking-widest text-[10px]">{k}</div>
                  <div className="flex-1 bg-[#1A1A1A] h-5 relative">
                    <div className="absolute inset-y-0 left-0 bg-[#FF1E1E]" style={{ width: `${(v / max) * 100}%` }} />
                  </div>
                  <div className="w-12 text-right text-white font-bold">{v}</div>
                  <div className="w-16 text-right text-white/45 text-[10px]">{i > 0 ? `-${dropoff}%` : ""}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Matching success */}
      {ms && (
        <div className="qd-card p-6 mb-6">
          <div className="qd-overline mb-4">Matching Engine Success</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A]">
            <StatTile label="Requirements" value={ms.requirements_total} />
            <StatTile label="With matches" value={ms.with_matches} accent />
            <StatTile label="Match rate" value={`${ms.match_rate}%`} accent />
            <StatTile label="Avg matches / req" value={ms.avg_matches_per_req} />
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifs && (
        <div className="qd-card p-6">
          <div className="qd-overline mb-4">Notifications Engagement</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A]">
            <StatTile label="Total sent" value={notifs.total} />
            <StatTile label="Seen" value={notifs.seen} accent />
            <StatTile label="Open rate" value={`${notifs.open_rate}%`} accent />
            <StatTile label="Match alerts" value={notifs.by_kind?.match || 0} />
          </div>
        </div>
      )}
    </div>
  );
}
